import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, RotateCcw, Save } from 'lucide-react';
import adminAiAnalysisModelService, {
  AiAnalysisAvailableModel,
  AiAnalysisModelSettingData,
  AiAnalysisModelUpdatePayload,
} from '../../services/api/adminAiAnalysisModelService';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyLabel,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';

/**
 * 1kトークン単価を $/1M 表記にする（0.005 → "$5"）。
 * 単価が取れないモデルはサーバーが 0 を返すので、その場合は表示しない
 */
const formatPerMillion = (perThousand: number | null | undefined): string | null => {
  if (perThousand === null || perThousand === undefined || Number.isNaN(perThousand) || perThousand <= 0) {
    return null;
  }
  return `$${Math.round(perThousand * 1000 * 100) / 100}`;
};

/** セレクトの選択肢ラベル（例: GPT-5.6 Sol（入力 $5 / 出力 $30 per 1M）） */
const modelOptionLabel = (model: AiAnalysisAvailableModel): string => {
  const input = formatPerMillion(model.input_per_1k);
  const output = formatPerMillion(model.output_per_1k);
  if (!input || !output) {
    return model.label;
  }
  return `${model.label}（入力 ${input} / 出力 ${output} per 1M）`;
};

const AiAnalysisModelPanel: React.FC = () => {
  const [data, setData] = useState<AiAnalysisModelSettingData | null>(null);
  const [defaultModel, setDefaultModel] = useState('');
  const [taskOverrides, setTaskOverrides] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // レスポンスをフォーム状態へ反映する（読込・保存後で共通）
  const applyResponse = useCallback((next: AiAnalysisModelSettingData) => {
    setData(next);
    setDefaultModel(next.setting?.default_model ?? '');
    const overrides: Record<string, string> = {};
    next.tasks.forEach((task) => {
      overrides[task.key] = task.override_model ?? '';
    });
    setTaskOverrides(overrides);
  }, []);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminAiAnalysisModelService.get();
      applyResponse(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'AI解析モデル設定の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [applyResponse]);

  useEffect(() => {
    load();
  }, [load]);

  const availableModels = useMemo(() => data?.available_models ?? [], [data]);

  const modelLabel = useCallback(
    (value: string | null): string => {
      if (!value) {
        return '';
      }
      return availableModels.find((model) => model.value === value)?.label ?? value;
    },
    [availableModels],
  );

  const configDefaultLabel = useMemo(
    () => modelLabel(data?.config_default_model ?? null) || '未設定',
    [data, modelLabel],
  );

  const handleSave = async () => {
    if (!data) {
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      setMessage(null);

      // 上書きが設定されているタスクだけ送る。解除されたタスクは null を明示して削除する
      const taskModels: Record<string, string | null> = {};
      data.tasks.forEach((task) => {
        const selected = taskOverrides[task.key] ?? '';
        if (selected) {
          taskModels[task.key] = selected;
        } else if (task.override_model) {
          taskModels[task.key] = null;
        }
      });

      const payload: AiAnalysisModelUpdatePayload = {
        default_model: defaultModel || null,
        task_models: taskModels,
      };

      const response = await adminAiAnalysisModelService.update(payload);
      applyResponse(response.data);
      setMessage('保存しました。60秒以内に全サーバーへ反映されます（デプロイ・再起動は不要）。');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'AI解析モデル設定の保存に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">AI解析モデル設定を読み込み中...</span>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      {message && <TaskyAlert tone="success">{message}</TaskyAlert>}
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-dark">AI解析モデル設定</h2>
          <p className="mt-2 text-sm text-gray-600">
            生成パイプラインのテキスト・画像解析（LP企画・UGC台本・バナー解析／プロンプト・商品情報の抽出・ABテスト仮説 など）で使うモデルをここで切り替えます。
            保存すればデプロイやワーカー再起動なしで全サーバーに反映されます。
          </p>
          <p className="mt-1 text-sm text-gray-600">
            AIチャット（/chat/ai）のモデルは別系統です。AIエージェント管理のエージェント設定で変更してください。
          </p>
        </div>
        <TaskyButton
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !data}
          data-track="admin-ai-analysis-model-save"
        >
          {isSaving ? <RefreshCw className="animate-spin" /> : <Save />}
          保存
        </TaskyButton>
      </div>

      {data && !data.routing_enabled && (
        <TaskyAlert tone="warning">
          この環境では解析ルーティング（AI_ANALYSIS_ROUTING_ENABLED）が無効のため、この設定は使われません。
        </TaskyAlert>
      )}

      <div className="rounded-tasky-md border border-warm-border bg-white p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <div>
            <TaskyLabel htmlFor="ai-analysis-default-model">基本モデル</TaskyLabel>
            <TaskySelect
              id="ai-analysis-default-model"
              value={defaultModel}
              onChange={(event) => setDefaultModel(event.target.value)}
            >
              <option value="">設定ファイルの既定（{configDefaultLabel}）</option>
              {availableModels.map((model) => (
                <option key={model.value} value={model.value}>
                  {modelOptionLabel(model)}
                </option>
              ))}
            </TaskySelect>
            <p className="mt-2 text-xs text-gray-600">
              モデルを固定していないタスクがこの基本モデルに従います。
            </p>
          </div>

          <div className="flex flex-col justify-end gap-2 text-sm text-gray-600">
            <div>
              <TaskyButton
                variant="secondary"
                size="sm"
                onClick={() => setDefaultModel('')}
                disabled={defaultModel === ''}
                data-track="admin-ai-analysis-model-reset-default"
              >
                <RotateCcw />
                基本モデルを既定に戻す
              </TaskyButton>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">現在の基本モデル</span>
              <TaskyBadge variant="info">{modelLabel(data?.effective_default_model ?? null) || '未設定'}</TaskyBadge>
            </div>
            {data?.setting?.updated_at && (
              <p className="text-xs text-gray-500">
                最終更新: {new Date(data.setting.updated_at).toLocaleString('ja-JP')}
              </p>
            )}
          </div>
        </div>
      </div>

      <TaskyTable className="min-w-[1000px]">
        <TaskyTableHead>
          <TaskyTableRow hoverable={false}>
            <TaskyTableHeaderCell>タスク</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>キー</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>設定</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>上書き</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>現在の有効モデル</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>effort</TaskyTableHeaderCell>
            <TaskyTableHeaderCell align="right">最大出力トークン</TaskyTableHeaderCell>
          </TaskyTableRow>
        </TaskyTableHead>
        <TaskyTableBody>
          {(data?.tasks ?? []).map((task) => (
            <TaskyTableRow key={task.key}>
              <TaskyTableCell className="whitespace-nowrap">{task.label}</TaskyTableCell>
              <TaskyTableCell className="font-mono text-xs text-gray-500">{task.key}</TaskyTableCell>
              <TaskyTableCell className="whitespace-nowrap text-sm text-gray-600">
                {task.pinned_model ? modelLabel(task.pinned_model) : '基本モデルに従う'}
              </TaskyTableCell>
              <TaskyTableCell className="min-w-[220px]">
                <TaskySelect
                  aria-label={`${task.label}の上書きモデル`}
                  value={taskOverrides[task.key] ?? ''}
                  onChange={(event) =>
                    setTaskOverrides((prev) => ({ ...prev, [task.key]: event.target.value }))
                  }
                >
                  <option value="">上書きしない</option>
                  {availableModels.map((model) => (
                    <option key={model.value} value={model.value}>
                      {modelOptionLabel(model)}
                    </option>
                  ))}
                </TaskySelect>
              </TaskyTableCell>
              <TaskyTableCell className="whitespace-nowrap">
                <TaskyBadge variant="warm">{modelLabel(task.effective_model) || '未設定'}</TaskyBadge>
              </TaskyTableCell>
              <TaskyTableCell className="whitespace-nowrap text-sm text-gray-600">
                {task.reasoning_effort ?? '-'}
              </TaskyTableCell>
              <TaskyTableCell align="right" className="whitespace-nowrap text-sm text-gray-600">
                {task.max_output_tokens !== null && task.max_output_tokens !== undefined
                  ? task.max_output_tokens.toLocaleString()
                  : '-'}
              </TaskyTableCell>
            </TaskyTableRow>
          ))}
        </TaskyTableBody>
      </TaskyTable>
    </section>
  );
};

export default AiAnalysisModelPanel;
